import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';
import { environment } from '../environments/environment';

export interface EmailConfig {
    alert_name: string;
    send_to: string[] | null;
    send_cc: string[] | null;
    send_bcc: string[] | null;
    send_from: string;
}

@Injectable({
    providedIn: 'root',
})
export class AdminService {
    private apiUrl = environment.apiUrl;

    private emailConfig$: EmailConfig[] | null = null;

    constructor(private http: HttpClient) {}

    /**
     * Fetches the email configuration for alerts from the backend API.
     * @returns An Observable that emits the EmailConfig object containing alert email settings.
     */
    public getEmailConfig(): Observable<EmailConfig[]> {
        if (this.emailConfig$) {
            return of(this.emailConfig$);
        }
        return this.http
            .get<EmailConfig[]>(`${this.apiUrl}/admin/getEmailConfig`)
            .pipe(tap((config) => (this.emailConfig$ = config)));
    }

    /**
     * Updates the email configuration for alerts by sending the new settings to the backend API.
     * @param config The new email configuration settings to be applied.
     * @returns An Observable that completes when the email configuration has been successfully updated on the backend.
     */
    public updateEmailConfig(config: EmailConfig): Observable<void> {
        return this.http.patch<void>(
            `${this.apiUrl}/admin/updateEmailConfig`,
            config,
        );
    }

    public getDownloadUrl(key: string): string {
        return `${this.apiUrl}/deliveries/download?key=${encodeURIComponent(key)}`;
    }

    public getMaintenanceStatus(): Observable<{ status: number }> {
        return this.http.get<{ status: number }>(
            `${this.apiUrl}/admin/getMaintenanceStatus`,
            { withCredentials: true },
        );
    }

    public setMaintenanceStatus(status: number): Observable<void> {
        return this.http.post<void>(
            `${this.apiUrl}/admin/setMaintenanceStatus`,
            { status },
            { withCredentials: true },
        );
    }

    /** Fetch only freight forwarder companies (for the FF assignment dropdown). */
    public getFreightForwarders(): Observable<
        { id: number; name: string; code: string }[]
    > {
        return this.http
            .get<{
                companies: { id: number; name: string; code: string }[];
            }>(`${this.apiUrl}/users/getFreightForwarders`, {
                withCredentials: true,
            })
            .pipe(map((res) => res.companies));
    }
}
